import { useEffect, useMemo, useRef, useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ShieldQuestion } from "lucide-react";
import { FeedbackState, type DataTableColumn } from "@h2j-data-team/ui";
import type { UnloadedColumnsQvd } from "@repo/shared";

import { formatBytes } from "@/lib/health-format";
import { qvdUnloadedColumnsInfiniteQueryOptions } from "@/features/qvd-analysis/api";
import { VirtualRowList } from "@/components/shared/VirtualRowList";
import {
  FileFormatBadge,
  ListError,
  ListLoading,
  QvdPathLabel,
  RowButton,
  SpaceLink,
} from "./qvd-list-parts";
import { QvdConsumingAppsDrawer } from "./QvdConsumingAppsDrawer";

// The QVDs the analysis could NOT judge — by EITHER source. One of their
// consuming apps left no field evidence (its reload log could not be read) and
// Qlik's field lineage could not be asked what that app loads either (no lineage
// node, a failed query, or the run's lineage call budget), so no column may be
// called unloaded and no saving is claimed. That second half is what makes this
// list short: a mute app alone only sends the QVD to the lineage fallback, and
// only a QVD both sources failed lands here. They are deliberately kept out of the
// unloaded-columns list — that list means "these QVDs have unloaded columns",
// and a withheld verdict is the opposite claim. Each row opens the SAME
// consuming-apps drawer, which is where the user sees which app is mute and
// why (no permission / no reload log available / unreadable) — the actionable
// part.
export function EvidenceIncompleteTable({
  runId,
  baseUrl,
}: {
  runId: string;
  baseUrl?: string | null;
}) {
  const { t } = useTranslation("qvd-analysis");
  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery(qvdUnloadedColumnsInfiniteQueryOptions(runId, "incomplete"));

  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage && !isError) fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, isError, fetchNextPage]);

  // Kept after closing so the drawer keeps its content while it animates out;
  // the button that opened it gets the focus back.
  const [detailRow, setDetailRow] = useState<UnloadedColumnsQvd | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const opener = useRef<HTMLElement | null>(null);

  const rows = useMemo(() => data?.pages.flatMap((p) => p.data) ?? [], [data]);

  const columns = useMemo<DataTableColumn<UnloadedColumnsQvd>[]>(
    () => [
      {
        id: "qvd",
        header: t("evidence.cols.qvd"),
        rowLink: true,
        cell: (r) => (
          <span className="flex min-w-0 items-center gap-2">
            <span className="min-w-0 flex-1">
              <RowButton
                label={t("columns.consumers.openLabel", { name: r.qvdName })}
                onClick={(button) => {
                  opener.current = button;
                  setDetailRow(r);
                  setDetailOpen(true);
                }}
              >
                <QvdPathLabel path={r.qvdPath} />
              </RowButton>
            </span>
            <FileFormatBadge path={r.qvdPath} />
          </span>
        ),
      },
      {
        id: "space",
        header: t("evidence.cols.space"),
        width: 180,
        cell: (r) => (
          <span className="text-dim">
            <SpaceLink spaceName={r.spaceName} spaceId={r.spaceId} baseUrl={baseUrl} />
          </span>
        ),
      },
      {
        id: "size",
        header: t("evidence.cols.size"),
        width: 110,
        align: "end",
        variant: "numeric",
        cell: (r) => (
          <span className="text-dim">{r.totalBytes != null ? formatBytes(r.totalBytes) : "—"}</span>
        ),
      },
    ],
    [t, baseUrl],
  );

  if (isLoading) return <ListLoading />;
  if (isError) {
    return <ListError error={error} title={t("evidence.error")} onRetry={() => void refetch()} />;
  }
  // Reachable ONLY when the tab's counter and the rows disagree: the tab is
  // rendered from coverage.evidenceIncompleteCount, which is saved after the
  // rows, so a run cancelled in between shows a positive badge over an empty
  // list. The copy says that, instead of claiming every QVD got a verdict and
  // contradicting the badge two lines above it.
  if (rows.length === 0) {
    return (
      <FeedbackState
        state="empty"
        icon={<ShieldQuestion />}
        title={t("evidence.empty.title")}
        description={t("evidence.empty.description")}
      />
    );
  }

  return (
    <div className="flex flex-col">
      <p className="pb-2 text-sm text-dim">{t("evidence.summary", { count: rows.length })}</p>

      <VirtualRowList
        label={t("tabs.evidence")}
        columns={columns}
        rows={rows}
        getKey={(r) => r.id}
        linked={() => true}
      />

      <QvdConsumingAppsDrawer
        open={detailOpen}
        onOpenChange={setDetailOpen}
        runId={runId}
        row={detailRow}
        restoreFocusRef={opener}
      />
    </div>
  );
}
